import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * One filter held in the URL, over any `read`/`write`/`clear` round trip a domain module
 * offers (`domain/rowFilter.ts`, `domain/auditFilter.ts`): read the current params, patch or
 * clear them. Writes replace the history entry, so typing a search does not leave one
 * back-button step per keystroke.
 *
 * `patch`/`clear` are `useCallback`s with an EMPTY deps array, on purpose: a caller
 * (`StudiosPage`/`GamesPage`) memoises an `actions` element and, to keep that memo from
 * rebuilding on every unrelated render, lists `patch` out of its own deps — which only works
 * if `patch`'s reference (and behaviour) never goes stale. `setSearchParams` from
 * `useSearchParams()` is not itself stable across renders, so closing over it directly (the
 * previous shape) meant a `patch` built on one render could still be the one an old memoised
 * `actions` element called on a LATER render — and react-router 6's functional updater form
 * runs the updater against the `searchParams` snapshot `setSearchParams` itself captured at
 * ITS OWN render, not necessarily the latest URL, so a stale closure could rebuild the query
 * from an old snapshot and drop whatever changed since (repro: type "a", pick status=drift,
 * type "b" — status must survive). Keeping the latest `setSearchParams` in a ref and reading
 * it inside a `[]`-deps callback keeps `patch`/`clear` referentially stable AND always current.
 */
export const useUrlFilter = <F>(
    read: (params: URLSearchParams) => F,
    write: (params: URLSearchParams, patch: Partial<F>) => URLSearchParams,
    clear: (params: URLSearchParams) => URLSearchParams,
) => {
    const [params, setParams] = useSearchParams();
    const filters = read(params);

    const setParamsRef = useRef(setParams);
    setParamsRef.current = setParams;

    const patch = useCallback(
        (next: Partial<F>) => setParamsRef.current((current) => write(current, next), { replace: true }),
        // `write` is a pure module-level function per caller (`readRowFilter`/`writeRowFilter`
        // etc. never change identity across renders), so it is safe to leave out of deps too —
        // the point of this callback is to be permanently stable, not merely `useMemo`-cheap.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const clearFilters = useCallback(
        () => setParamsRef.current((current) => clear(current), { replace: true }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return { filters, patch, clear: clearFilters };
};
